import { Box, Flex, Grid, Group, Stack, Switch, Title, Indicator as IndicatorBadge } from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import { useState } from 'react';
import { useMeasurementReportDownload } from 'hooks/api/useReports';
import { getGraphData } from 'utils/charts';
import { useWindowSize } from 'hooks/useWindowSize';
import { PpgChart } from 'components/Charts/PpgChart/PpgChart';
import { RrPcTachogram } from 'components/Charts/PpgRrPcTachogram/PpgRrPcTachogram';
import { InfoIcon } from 'components/InfoIcon';
import { CenteredLoading } from 'components/CenteredLoading';
import { DrawerActionButton } from 'components/Table/Drawer/DrawerActionButton';
import { useComments } from 'services/comments';
import { BsArrowLeft, BsArrowRight, BsChatSquareText, BsDownload, BsPencilSquare } from 'react-icons/bs';
import { useTranslation } from 'react-i18next';
import { useHeartRhythmMeasurementDetails } from 'hooks/api/useHeartRhythmData';
import { useParameters } from 'hooks/useParameters';
import { queryParams } from 'utils/query';
import {
  getGraphHeight,
  PcChartOptions,
  PpgChartOptions,
  RrChartOptions,
} from 'components/Charts/ChartOptions/MeasurementDetailsChartOptions';
import { HeartRhythmMeasurementStatus } from 'models/heartRhythm/measurement';
import { Indicator } from 'utils/models';
import { MeasurementIsRevising } from './MeasurementIsRevising';
import { MeasurementDetailsInfo } from './MeasurementDetailsInfo';
import { MeasurementDetailsIsCommenting } from './MeasurementDetailsIsCommenting';
import { MeasurementDetailsBadges } from './MeasurementDetailsBadges';

interface MeasurementDetailsProps {
  moreIds?: string[];
}

export const MeasurementDetails = ({ moreIds }: MeasurementDetailsProps) => {
  const { t } = useTranslation();
  const { setParameters, parameters: filters } = useParameters();
  const { height: windowHeight } = useWindowSize();
  const { data: selectedMeasurement, isPending: isLoading } = useHeartRhythmMeasurementDetails({
    measurementId: filters[queryParams.detailId],
  });
  const [isRevising, setIsRevising] = useState(false);
  const [isCommenting, setIsCommenting] = useState(false);
  const [showAllDatapoints, setShowAllDatapoints] = useState(false);
  const { data: commentList } = useComments(selectedMeasurement?.id || '');
  const { ref: lorenzContainerRef, width: lorenzContainerWidth } = useElementSize();
  const lorenzMaxSize = getGraphHeight(windowHeight);
  const lorenzChartSize = lorenzContainerWidth > 0 ? Math.min(lorenzContainerWidth, lorenzMaxSize) : lorenzMaxSize;

  const currentIndex = moreIds?.findIndex((value) => value === selectedMeasurement?.id) || 0;

  const { mutate: downloadReport, isPending: isDownloading } = useMeasurementReportDownload();

  const handleNextMeasurement = () => {
    if (moreIds) setParameters({ ...filters, [queryParams.detailId]: moreIds[currentIndex + 1] });
  };

  const handlePreviousMeasurement = () => {
    if (moreIds) setParameters({ ...filters, [queryParams.detailId]: moreIds[currentIndex - 1] });
  };

  const handleDownloadMeasurementAction = () => {
    if (!selectedMeasurement?.id) return;
    downloadReport({ measurementId: selectedMeasurement.id });
  };

  const { ppg, peaks, flags } = selectedMeasurement?.data || {};
  const graphData = getGraphData(ppg, peaks, flags);

  if (!selectedMeasurement || isLoading) return <CenteredLoading />;

  if (isCommenting)
    return <MeasurementDetailsIsCommenting setIsCommenting={setIsCommenting} measurementId={selectedMeasurement.id} />;

  if (isRevising) return <MeasurementIsRevising setIsRevising={setIsRevising} measurementId={selectedMeasurement.id} />;

  return (
    <Box>
      <Flex justify={'space-between'}>
        <Title mb={'xl'} mt={'xs'} order={3}>
          {t('patients_measurement_detail')}
        </Title>
        <Group mb={'xl'} mt={'xs'}>
          {moreIds?.length && (
            <Group gap={'xs'}>
              <DrawerActionButton
                tooltipLabel={t('patients_previous_measurement')}
                disabled={currentIndex <= 0}
                onClick={() => handlePreviousMeasurement()}
                Icon={BsArrowLeft}
                isOutlineVariant
              />
              <DrawerActionButton
                tooltipLabel={t('patients_next_measurement')}
                disabled={currentIndex >= (moreIds?.length || 0) - 1}
                onClick={() => handleNextMeasurement()}
                Icon={BsArrowRight}
                isOutlineVariant
              />
            </Group>
          )}
          <Group gap={'xs'}>
            <IndicatorBadge
              offset={2}
              label={commentList?.length}
              size={20}
              color={'var(--mantine-color-turquoise-5)'}
              withBorder
            >
              <DrawerActionButton
                tooltipLabel={t('label_patient_comments')}
                onClick={() => setIsCommenting(true)}
                Icon={BsChatSquareText}
              />
            </IndicatorBadge>

            <DrawerActionButton
              tooltipLabel={t('label_patient_revise')}
              onClick={() => setIsRevising(true)}
              Icon={BsPencilSquare}
              disabled={selectedMeasurement.data.indicator === Indicator.Inconclusive}
            />
            <DrawerActionButton
              tooltipLabel={t('label_patient_download')}
              onClick={() => handleDownloadMeasurementAction()}
              Icon={BsDownload}
              loading={isDownloading}
            />
          </Group>
        </Group>
      </Flex>
      <MeasurementDetailsBadges />
      <MeasurementDetailsInfo measurementId={selectedMeasurement.id} />

      {selectedMeasurement?.status !== HeartRhythmMeasurementStatus.AnalysisFailed && (
        <Grid>
          <Grid.Col span={12}>
            <PpgChart
              chartOptions={PpgChartOptions(graphData.ppg, graphData.peaks, graphData.flags, windowHeight)}
              rawSignal={ppg?.signal}
            />
          </Grid.Col>
          <Grid.Col span={7} fw={700}>
            <Stack gap={'xs'} mih={64}>
              <Group gap={'xs'}>
                <Title order={4}>{t('patients_tachogram_title')}</Title>
                <InfoIcon i18nKey={'patients_tachogram_tooltip'} />
              </Group>
            </Stack>
            <RrPcTachogram chartOptions={RrChartOptions(graphData?.rr, lorenzChartSize)} />
          </Grid.Col>
          <Grid.Col span={5} fw={700}>
            <Box ref={lorenzContainerRef}>
              <Stack gap={'xs'} mih={64}>
                <Title order={4}>{t('patients_lorenz_title')}</Title>
                <Switch
                  label={t('patients_lorenz_show_all_datapoints')}
                  labelPosition={'right'}
                  color={'turquoise'}
                  checked={showAllDatapoints}
                  onChange={(event) => setShowAllDatapoints(event.currentTarget.checked)}
                  size={'xs'}
                />
              </Stack>
              <RrPcTachogram chartOptions={PcChartOptions(graphData?.rr, lorenzChartSize, showAllDatapoints)} />
            </Box>
          </Grid.Col>
        </Grid>
      )}
    </Box>
  );
};
